import { colors } from '@lmc/common/constants'
import { SAFEGUARDING_ITEM_ID } from '@lmc/common/types'
import type { Home, IncidentReportItem, Log } from '@lmc/ts-common'
import { Button, Icon, IconType, ResolveModal } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import Actions from 'actions'
import { gaEvent } from 'common/services/analytics'
import { getJSON } from 'common/services/api'
import useSafeguardingAccess from 'components/AccessControl/useSafeguardingAccess'
import { Spinner } from 'components/generic'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { selfServeConstants } from 'locale/selfServe'
import _, { find } from 'lodash'
import { type FC, useEffect, useState } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { getQueryParams } from 'utils/getQueryParams'
import Operations from '../../../operations'
import { EmptyItems, HomeCard } from './index'

const IncidentComponent = (
  incident: Pick<IncidentReportItem, 'serviceUser'> & {
    log: Pick<
      IncidentReportItem['log'],
      'id' | 'timeLogged' | 'title' | 'category' | 'item'
    >
  },
  index: number
) => {
  const picture = incident.log.item?.icon || incident.log.category.icon
  const dotStyle = {
    ...styles.dot,
    backgroundColor: incident.log.category.color
  }

  if (!incident.serviceUser) return null

  return (
    <Link
      key={index}
      onClick={() =>
        gaEvent({
          action: 'Click view_incident',
          label: undefined,
          category: undefined
        })
      }
      to={`/logs/${incident.log.id}`}
      style={{ width: '100%' }}
    >
      <div style={{ float: 'left' }}>
        <div style={dotStyle} className="lmc-timeline-dot">
          <div
            className="lmc-dot-icon"
            style={{ background: `url(${picture})`, ...styles.iconStyle }}
          />
        </div>
      </div>
      <p style={{ padding: '0 0 0 55px', color: 'black', marginTop: 0 }}>
        <span
          style={{ opacity: 0.8, fontSize: 16, overflowWrap: 'break-word' }}
        >
          {_.truncate(incident.log.title, { length: 80 })}
        </span>
        <br />
        <span style={{ opacity: 0.6, fontSize: 12 }}>
          {dayjs(incident.log.timeLogged).format('HH:mm')} -{' '}
          {incident.serviceUser.name}
        </span>
      </p>
    </Link>
  )
}

type Props = {
  hasPro?: boolean
  hasTrial?: boolean
}

export const IncidentsCard: FC<Props> = ({ hasTrial }) => {
  const [showConflictsModal, setShowConflictsModal] = useState(false)

  const dispatch = useDispatch()

  const { t } = useTranslation(NAMESPACE.SELF_SERVE)

  const features = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const category = useSelector(
    (state: { data: { 'log-categories': { itemId: string } } }) =>
      // @ts-ignore
      find(state.data['log-categories'], { itemId: 'incident' }) as {
        id: string
      }
  )

  const conflicts = useSelector(
    (state: { data: { conflicts: Log[] } }) => state.data.conflicts
  )

  const getConflicts = () => dispatch(Operations.loadList('conflicts'))

  const resolveConflict = (json: JSON) =>
    dispatch(
      Operations.createListItem({
        listId: 'conflicts',
        itemId: 'resolve',
        json
      })
    )

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (
      features?.features?.conflicts?.enabled &&
      typeof getConflicts === 'function'
    ) {
      getConflicts()
    }
  }, [])

  const query = {
    'pagination[itemsPerPage]': 2,
    'pagination[page]': 1
  }

  const { data, isLoading, error } = useQuery<{
    data: Array<IncidentReportItem>
  }>(['incident-list'], () =>
    getJSON({
      url: `list/incident-report/list-alerts?${getQueryParams(query || {})}`
    })
  )

  const safeguardingAccess = useSafeguardingAccess()

  if (error) {
    return (
      <HomeCard title={TITLE}>
        {'There was an error loading the alerts'}
      </HomeCard>
    )
  }

  const incidents = data?.data

  const filteredIncidents = !safeguardingAccess
    ? incidents?.filter(
        (incident) => incident.log.item?.id !== (SAFEGUARDING_ITEM_ID as any)
      )
    : incidents

  const renderNoIncidents = () => (
    <>
      {!hasTrial && (
        <div>
          <p style={{ marginTop: 0 }}>No alerts</p>
        </div>
      )}
      {hasTrial && (
        <>
          <EmptyItems items={2} size="lg" />
          <InfoWrapper>
            <StyledText>{t(selfServeConstants.YOU_WILL_RECEIVE)}</StyledText>
          </InfoWrapper>
        </>
      )}
    </>
  )

  const renderIncidents = (incidents?: IncidentReportItem[]) =>
    _(incidents)
      .take(2)
      .value()
      .map((data, index) => IncidentComponent(data, index))

  const handleModalVisible = (visible?: boolean) =>
    setShowConflictsModal(visible || !showConflictsModal)

  const renderConflicts = () => {
    const hasConflicts = !!conflicts?.length

    const isDesktop = isBrowser || isTablet

    return (
      <>
        {features?.features?.conflicts?.enabled && (
          <ResolveModal
            isMobileLayout={!isDesktop}
            onConflictResolve={resolveConflict}
            conflicts={conflicts}
            isModalVisible={showConflictsModal}
            setIsModalVisible={handleModalVisible}
            getConflicts={getConflicts}
            loadingSpinner={<Spinner />}
          />
        )}
        <div
          style={{
            opacity: hasConflicts ? 1 : 0,
            cursor: hasConflicts ? 'pointer' : 'default',
            ...styles.conflicts
          }}
          onClick={() => hasConflicts && setShowConflictsModal(true)}
        >
          <Icon customIcon={IconType.Attention} style={{ marginRight: 5 }} />
          {`${conflicts?.length} Conflicts`}
        </div>
      </>
    )
  }

  const setIncidentFilter = (categoryId: string) => {
    dispatch(
      Actions.toggleActiveFilter({
        filterId: 'logs',
        key: 'category',
        value: categoryId
      })
    )
  }

  const goToDashboard = () => {
    const categoryId = category && category.id
    if (categoryId) setIncidentFilter(categoryId)

    gaEvent({
      category: 'home-dashboard',
      action: 'Click view_all_incidents',
      label: undefined
    })
  }

  const renderFooter = () => {
    if (isLoading) return null
    return (
      <div style={styles.footer}>
        {renderConflicts()}
        {filteredIncidents?.length ? (
          <Link onClick={goToDashboard} to="/logs">
            <Button
              variant="default"
              style={{
                marginLeft: 'auto',
                marginRight: 0
              }}
            >
              {BUTTON_TEXT}
            </Button>
          </Link>
        ) : null}
      </div>
    )
  }

  const renderBody = () => {
    if (isLoading) return <Spinner style={{ paddingTop: 40 }} />

    if (filteredIncidents && filteredIncidents.length) {
      return renderIncidents(filteredIncidents)
    }

    return renderNoIncidents()
  }

  return (
    <HomeCard title={TITLE} footer={renderFooter()}>
      <div style={{ height: 170 }}>{renderBody()}</div>
    </HomeCard>
  )
}

const TITLE = 'Incident Alerts'

const BUTTON_TEXT = 'View All'

const styles = {
  dot: {
    left: -20,
    top: -18,
    width: 42,
    height: 42,
    border: '3px solid rgba(0,0,0,0)',
    borderRadius: 24,
    backgroundColor: colors.grey,
    alignItems: 'center'
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  iconStyle: {
    backgroundSize: '14px !important',
    backgroundPosition: 'center center !important'
  },
  title: {
    opacity: 0.8
  },
  conflicts: {
    color: colors.red,
    textDecoration: 'underline',
    marginTop: 5,
    display: 'flex',
    alignItems: 'center'
  }
}

const InfoWrapper = styled.div`
  background-color: #fff8fa;
  border-radius: 16px;
  padding: 16px;
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

export default IncidentsCard
